/**
 * <vecos-ticker>: cinta que se desliza de derecha a izquierda y que el usuario
 * también puede arrastrar con el dedo o la rueda del ratón.
 *
 * Modos (data-mode):
 * - "continuous" (por defecto): movimiento continuo a data-speed px/s.
 * - "step": muestra data-visible elementos y avanza de uno en uno cada data-interval segundos.
 *
 * Se pausa al pasar el ratón, al tocarla, al enfocar un enlace o al reproducir
 * un vídeo, y no se mueve si el sistema pide reducir el movimiento.
 */
if (!customElements.get('vecos-ticker')) {
  class VecosTicker extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-ticker-track]');
      if (!this.track) return;

      this.originals = Array.from(this.track.children);
      if (!this.originals.length) return;

      this.mode = this.dataset.mode === 'step' ? 'step' : 'continuous';
      this.speed = parseFloat(this.dataset.speed) || 30;
      this.visible = parseInt(this.dataset.visible, 10) || 4;
      this.interval = (parseFloat(this.dataset.interval) || 3) * 1000;
      this.reasons = new Set();
      this.pos = 0;
      this.loop = 0;
      this.last = null;
      this.reduced =
        window.matchMedia('(prefers-reduced-motion: reduce)').matches || this.dataset.autoplay === 'false';
      if (this.reduced) return;

      this.build();
      this.bind();
      if (document.hidden) this.reasons.add('hidden');

      if (this.mode === 'step') {
        this.timer = setInterval(this.step, this.interval);
      } else {
        this.raf = requestAnimationFrame(this.tick);
      }
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.raf);
      clearInterval(this.timer);
      clearTimeout(this.resumeTimer);
      clearTimeout(this.rebuildTimer);
      clearTimeout(this.wrapTimer);
      this.io?.disconnect();
      this.ro?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    build() {
      this.lastWidth = this.track.clientWidth;
      this.track.querySelectorAll('[data-ticker-clone]').forEach((clone) => clone.remove());
      this.loop = 0;

      if (this.mode === 'step') {
        // Con los visibles al final repetidos, el salto del último al primero no se nota.
        if (this.originals.length <= this.visible) return;
        const clones = this.originals.slice(0, this.visible).map((item) => this.clone(item));
        clones.forEach((clone) => this.track.appendChild(clone));
        this.loop = clones[0].offsetLeft - this.originals[0].offsetLeft;
      } else {
        const first = this.originals[0];
        const last = this.originals[this.originals.length - 1];
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        const period = last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
        if (period <= 0) return;

        // Repetimos el contenido hasta cubrir el ancho visible y lo duplicamos para que el bucle no tenga saltos.
        const reps = Math.max(1, Math.ceil(this.track.clientWidth / period));
        for (let i = 1; i < reps * 2; i++) {
          this.originals.forEach((item) => this.track.appendChild(this.clone(item)));
        }
        this.loop = period * reps;
      }

      if (this.loop) {
        this.pos = this.pos % this.loop;
        this.track.scrollLeft = this.pos;
      }
    }

    clone(item) {
      const copy = item.cloneNode(true);
      copy.setAttribute('data-ticker-clone', '');
      copy.setAttribute('aria-hidden', 'true');
      copy.removeAttribute('data-shopify-editor-block');
      copy.querySelectorAll('a, button, video, iframe, input').forEach((el) => el.setAttribute('tabindex', '-1'));
      copy.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
      return copy;
    }

    bind() {
      const pause = (reason) => this.setPaused(reason, true);
      const resume = (reason) => this.setPaused(reason, false);

      this.addEventListener('pointerenter', (event) => event.pointerType === 'mouse' && pause('hover'));
      this.addEventListener('pointerleave', (event) => event.pointerType === 'mouse' && resume('hover'));
      this.addEventListener('focusin', () => pause('focus'));
      this.addEventListener('focusout', () => resume('focus'));
      this.addEventListener('play', () => pause('media'), true);
      this.addEventListener('pause', () => resume('media'), true);
      this.addEventListener('ended', () => resume('media'), true);

      this.track.addEventListener(
        'touchstart',
        () => {
          clearTimeout(this.resumeTimer);
          pause('user');
        },
        { passive: true }
      );
      this.track.addEventListener('touchend', () => this.scheduleResume(), { passive: true });
      this.track.addEventListener(
        'wheel',
        (event) => {
          if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
          pause('user');
          this.scheduleResume();
        },
        { passive: true }
      );
      this.track.addEventListener('scroll', () => this.reasons.has('user') && this.scheduleResume(), {
        passive: true,
      });

      this.onVisibility = () => this.setPaused('hidden', document.hidden);
      document.addEventListener('visibilitychange', this.onVisibility);

      this.io = new IntersectionObserver(([entry]) => this.setPaused('offscreen', !entry.isIntersecting));
      this.io.observe(this);

      const rebuild = () => {
        clearTimeout(this.rebuildTimer);
        this.rebuildTimer = setTimeout(() => this.build(), 150);
      };
      this.ro = new ResizeObserver(() => this.track.clientWidth !== this.lastWidth && rebuild());
      this.ro.observe(this.track);
      document.fonts?.ready.then(rebuild);
      this.originals.forEach((item) =>
        item
          .querySelectorAll('img')
          .forEach((img) => !img.complete && img.addEventListener('load', rebuild, { once: true }))
      );
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.setPaused('user', false), 1500);
    }

    setPaused(reason, paused) {
      const wasPaused = this.reasons.size > 0;
      if (paused) this.reasons.add(reason);
      else this.reasons.delete(reason);

      if (wasPaused && this.reasons.size === 0 && this.loop) {
        // Retomamos desde donde lo haya dejado el usuario, en la posición equivalente del bucle.
        this.pos = this.track.scrollLeft % this.loop;
        this.track.scrollTo({ left: this.pos, behavior: 'instant' });
        this.last = null;
      }
    }

    // Modo continuo: avanza unos píxeles en cada fotograma.
    tick = (time) => {
      this.raf = requestAnimationFrame(this.tick);
      const delta = this.last === null ? 0 : Math.min(time - this.last, 64) / 1000;
      this.last = time;
      if (this.reasons.size || !this.loop) return;

      this.pos += this.speed * delta;
      if (this.pos >= this.loop) this.pos -= this.loop;
      this.track.scrollLeft = this.pos;
    };

    // Modo paso a paso: avanza un elemento y, al llegar a las copias, vuelve al inicio sin que se note.
    step = () => {
      if (this.reasons.size || !this.loop) return;

      const stepWidth = this.originals[1].offsetLeft - this.originals[0].offsetLeft;
      if (stepWidth <= 0) return;

      const target = (Math.round(this.track.scrollLeft / stepWidth) + 1) * stepWidth;
      this.track.scrollTo({ left: target, behavior: 'smooth' });

      clearTimeout(this.wrapTimer);
      this.wrapTimer = setTimeout(() => {
        if (this.track.scrollLeft >= this.loop - 1) {
          this.track.scrollTo({ left: this.track.scrollLeft - this.loop, behavior: 'instant' });
        }
      }, 800);
    };
  }

  customElements.define('vecos-ticker', VecosTicker);
}
